import { Organization } from 'models/organization';
import { RestorationInput } from 'models/order-input/restoration-input';
import { simpleCategoryForType } from 'components/order-flow/shared/restoration-input-helper';
export const disableOrderDeliveryDates = (date: Date, lab: Organization) => {
    const disabledDates = lab.holidayDays || [];
    const disabledDateStrings = disabledDates.map((day) => new Date(day).toDateString());

    // Get the weekday for the given date (0 = Sunday, 6 = Saturday)
    const weekday = date.getDay();

    let disabledWeekDays: (number | undefined)[] = [];
    if (lab.openingHours?.length) {
        disabledWeekDays = lab.openingHours
            .map((dayObj, index) => {
                let day = index + 1; // Convert index (0-based) to day number
                day = day === 7 ? 0 : day; // Adjust for Sunday (0)
                return { ...dayObj, dayNum: day };
            })
            .filter((dayObj) => !dayObj.active)
            .map((dayObj) => dayObj.dayNum);
    }

    // Check if the date matches any of the disabled dates
    const isDateDisabled = disabledDateStrings.includes(date.toDateString());

    // Check if the weekday matches any of the disabled weekdays
    const isWeekdayDisabled = disabledWeekDays.includes(weekday);

    return isDateDisabled || isWeekdayDisabled;
};

// Recursive helper to find the next valid delivery day
const findNextWorkingDay = (day: number, holidayDays: number[], inActiveDays: number[]): number => {
    const isHoliday = holidayDays.includes(day);
    const isClosed = inActiveDays.includes(new Date(day).getDay());
    if (isHoliday || isClosed) {
        return findNextWorkingDay(day + 86400000, holidayDays, inActiveDays);
    }
    return day;
};

// Counts forward N valid working days
const countDeliveryDays = (daysRequired: number, holidayDays: number[], inActiveDays: number[]) => {
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    let timestamp = day.getTime();

    while (daysRequired > 0) {
        const isHoliday = holidayDays.includes(timestamp);
        const isClosed = inActiveDays.includes(new Date(timestamp).getDay());
        if (!isHoliday && !isClosed) {
            daysRequired--;
        }
        timestamp += 86400000; // +1 day
    }

    return findNextWorkingDay(timestamp, holidayDays, inActiveDays);
};

export const getPerfectDeliveryDate = (lab: Organization, restorations: RestorationInput[]) => {
    if (!lab) return null;

    const activeJobs = lab.currentJobs?.filter((j) => j.active);

    // Match selected constructions with lab jobs (case-insensitive)
    let maxDeliveryDays = 0;
    for (const restoration of restorations) {
        const category = simpleCategoryForType(restoration.type);
        const job = activeJobs.find((j) => j.name.toLowerCase() === category.toLowerCase());
        if (job && job.deliveryDays > maxDeliveryDays) {
            maxDeliveryDays = job.deliveryDays;
        }
    }

    // Find inactive weekdays (where lab.openingHours[day].active === false)
    const inActiveDays = lab.openingHours
        .map((d, i) => (!d.active ? (i + 1) % 7 : null))
        .filter((d): d is number => d !== null);

    // Holidays dates
    const holidays = lab.holidayDays || [];

    const deliveryTimestamp = countDeliveryDays(maxDeliveryDays + 1, holidays, inActiveDays);
    return new Date(deliveryTimestamp);
};
